import {
  DSConversationIcon,
  Box,
  Col,
  DSIconArchive,
  DSIconAttachment,
  DSIconBookmark,
  DSIconButton,
  DSIconCrown,
  DSIconDoc,
  DSIconEye,
  DSIconHistory,
  DSIconProperties,
  DSIconTrash,
  Row,
  Text,
} from "@cdx/ds";
import {MutableRefObject, ReactNode, forwardRef} from "react";
import {ConvoPreview, getConvoPreviewInfo} from "../conversations/ConversationHint";
import useMutation from "../../lib/hooks/useMutation";
import {ArrowOverlay, TooltipForChild, cx, useGlobalKeyPress, useNextDropDown} from "@cdx/common";
import {sideButtonStyles} from "./unified-card-container.css";
import {openSidePanel, useDismissNotificationHandler} from "./card-container-utils";
import {Location, History} from "history";
import {getBookmarkInfo} from "../hand-queue/hand-queue-utils";
import {addCardsAsBookmark, removeCardsAsBookmark} from "./performHandMoveAction";
import {getParentCard, hasChildCards} from "../workflows/workflow-utils";
import performArchiveAction, {
  peformCardDeletion as rawPeformCardDeletion,
} from "./performArchiveAction";
import dsStyles from "@cdx/ds/css/index.css";
import RawAvatar from "../../components/Avatar";
import {Card, CardId} from "../../cdx-models/Card";
import {Root} from "../../cdx-models/Root";
import {CardSubscription} from "../../cdx-models/CardSubscription";
import {ArenaCtx} from "../card-panel/card-panel-utils";
import {CardPickerOverlay} from "../selection-header/AddToParentMenu";
import {Notifications} from "../notifications/useNotifications";
import {addIntention} from "../../lib/last-intention";

const peformCardDeletion = rawPeformCardDeletion as any;
const Avatar = RawAvatar as any;

type ShortcutInfo = {
  key: string;
  description: string;
};
type SidepanelButtonPassthroughProps = {
  active: boolean;
  sidePanelKey: string;
  category: string;
  history: History;
  location: Location;
};
type SidepanelButtonVariant = "nonEmpty";
type SidepanelButtonProps = SidepanelButtonPassthroughProps & {
  tooltip: null | string | (() => JSX.Element);
  tooltipOptions?: {bg?: string; hidden?: boolean};
  shortcut: ShortcutInfo;
  icon: ReactNode;
  variant?: SidepanelButtonVariant;
};
const SidepanelButton = forwardRef<HTMLElement, SidepanelButtonProps>((props, ref) => {
  const {icon, sidePanelKey, active, shortcut, category, history, location, variant} = props;
  const {tooltip, tooltipOptions} = props;
  useGlobalKeyPress({
    key: shortcut.key,
    fn: () => openSidePanel(history, location, sidePanelKey, active),
    description: shortcut.description,
    category,
  });
  return (
    <Box relative>
      <TooltipForChild tooltip={tooltip} {...tooltipOptions} placement="right" ref={ref}>
        <DSIconButton
          icon={icon}
          active={active}
          to={{
            ...location,
            state: {...(location.state as {}), sidePanelKey: active ? null : sidePanelKey},
          }}
          variant="secondary"
          size="md"
          className={cx(variant && sideButtonStyles.button.variant[variant])}
        />
      </TooltipForChild>
      {active && <div className={sideButtonStyles.arrow} />}
    </Box>
  );
});

export const PropPanelButton = (props: SidepanelButtonPassthroughProps) => {
  return (
    <SidepanelButton
      icon={<DSIconProperties />}
      tooltip="Edit properties"
      shortcut={{key: "z", description: "Open property panel"}}
      {...props}
    />
  );
};

type AttachmentPanelButtonProps = {
  tooltip: string;
  variant: SidepanelButtonVariant;
  blingRef: MutableRefObject<HTMLElement | null>;
} & SidepanelButtonPassthroughProps;
export const AttachmentPanelButton = (props: AttachmentPanelButtonProps) => {
  const {tooltip, variant, blingRef, ...rest} = props;
  return (
    <SidepanelButton
      icon={<DSIconAttachment />}
      tooltip={tooltip}
      shortcut={{
        key: "a",
        description:
          "Open attachments panel. Opens first image if attachment panel is opened already.",
      }}
      variant={variant}
      ref={blingRef}
      {...rest}
    />
  );
};

type ConvoPanelButtonProps = {
  card: Card;
  root: Root;
  notifications: Notifications;
} & SidepanelButtonPassthroughProps;
export const ConvoPanelButton = ({card, root, notifications, ...rest}: ConvoPanelButtonProps) => {
  const convoInfo = getConvoPreviewInfo({notifications, card, root});
  const hasPastConvos = card.$meta.exists("resolvables", {isClosed: true});
  const commentResCount = card.$meta.count("resolvables", {isClosed: false});
  const resNotis = (notifications && notifications.perCardResolvables[card.cardId as CardId]) || [];
  const hasNewEntries = resNotis.some((n: any) => n.unseenEntryCount || !n.isParticipating);
  return (
    <SidepanelButton
      icon={
        <DSConversationIcon
          size={null}
          convoInfo={convoInfo}
          hasPastConvos={hasPastConvos}
          active={rest.active}
        />
      }
      tooltip={
        convoInfo
          ? () => <ConvoPreview cardId={card.cardId as CardId} convoInfo={convoInfo} />
          : "Conversations"
      }
      tooltipOptions={convoInfo ? {bg: "gray200", hidden: rest.active} : {}}
      shortcut={{key: "n", description: "Toggle conversations panel"}}
      variant={hasNewEntries || commentResCount ? "nonEmpty" : undefined}
      {...rest}
    />
  );
};

// being used by both journey steps and card
type HistoryPanelButtonProps = {
  card: Card;
  root: Root;
  notifications?: Notifications;
} & SidepanelButtonPassthroughProps;
export const HistoryPanelButton = ({
  card,
  root,
  notifications,
  ...rest
}: HistoryPanelButtonProps) => {
  const hasCardDiffs = Boolean(notifications?.perCardDiffs[card.cardId as CardId]);
  const [doDismissRes] = useMutation("notifications", "dismissResolvables");
  const meId = root.loggedInUser?.id;

  const handleDismissNotifications = useDismissNotificationHandler({root, card, notifications});

  useGlobalKeyPress({
    code: "KeyX",
    withShift: true,
    fn: handleDismissNotifications,
    description: "Dismiss all non-conversation notifications associated with this card",
    category: "Card detail view",
    disabled: !notifications,
  });
  useGlobalKeyPress({
    code: "KeyX",
    withAlt: true,
    fn: () => {
      handleDismissNotifications();
      const cardRes = notifications?.perCardResolvables[card.cardId as CardId];
      const resIds =
        cardRes && cardRes.map((r: any) => r.$meta.get("resolvableId", null)).filter(Boolean);
      if (meId && resIds && resIds.length) {
        return doDismissRes({resolvableIds: resIds, userId: meId});
      }
    },
    description: "Dismiss all notifications associated with this card (normal + conversations)",
    category: "Card detail view",
    disabled: !notifications,
  });

  return (
    <SidepanelButton
      icon={<DSIconHistory />}
      tooltip="View the card history"
      shortcut={{key: "i", description: "Open history panel"}}
      variant={hasCardDiffs ? "nonEmpty" : undefined}
      {...rest}
    />
  );
};

type ChildCardPanelButtonProps = {
  card: Card;
  root: Root;
} & SidepanelButtonPassthroughProps;
export const ChildCardPanelButton = (props: ChildCardPanelButtonProps) => {
  const {card, root, ...rest} = props;
  const isChildCard = Boolean(getParentCard(card));

  const {ref, toggle, overlayElement, isOpen} = useNextDropDown({
    overlayProps: {
      placement: "right",
      distanceFromAnchor: 10,
      renderOverlay: ({close, ...overlayProps}: any) => (
        <ArrowOverlay {...overlayProps}>
          <Col>
            <Col px="16px" pt="8px">
              <Text type="label14" color="primary" as="h2">
                Set parent card
              </Text>
            </Col>
            <CardPickerOverlay
              onDone={close}
              childCardIds={[card.cardId]}
              cards={[card]}
              root={root}
              limit={5}
            />
          </Col>
        </ArrowOverlay>
      ),
    },
  });

  useGlobalKeyPress({
    code: "KeyS",
    withShift: true,
    fn: toggle,
    description: "Quick Select Parent Card",
    category: rest.category,
  });

  useGlobalKeyPress({
    code: "KeyC",
    withShift: true,
    fn: () => {
      openSidePanel(rest.history, rest.location, rest.sidePanelKey, rest.active);
      addIntention({type: "addSubCard"});
    },
    description: "Add Sub Card",
    category: rest.category,
  });

  return (
    <>
      {overlayElement}

      <SidepanelButton
        ref={ref}
        icon={<DSIconCrown />}
        tooltip={isOpen ? null : isChildCard ? "Manage Hero and sub cards" : "View sub cards"}
        shortcut={{
          key: "s",
          description:
            "Open Child Card Panel. Hitting 's' a second time will open sub cards in the main view.",
        }}
        variant={isChildCard ? "nonEmpty" : undefined}
        {...rest}
      />
    </>
  );
};

export const ChildCardPanelButtonForCardCreation = (props: SidepanelButtonPassthroughProps) => {
  return (
    <SidepanelButton
      icon={<DSIconCrown />}
      tooltip="Assign to Hero Card"
      shortcut={{
        key: "s",
        description: "Open Child Card Panel.",
      }}
      {...props}
    />
  );
};

export const BookmarkBottomButton = ({root, card}: {root: Root; card: Card}) => {
  const {loggedInUser} = root;
  const {isActive, disabled, tooltip} = getBookmarkInfo({card, root});

  const handleClick = () => {
    if (disabled) return;
    if (isActive) {
      return removeCardsAsBookmark({cards: [card], loggedInUser});
    } else {
      return addCardsAsBookmark({cards: [card], loggedInUser});
    }
  };

  useGlobalKeyPress({
    key: ".",
    fn: () => {
      if (!handleClick()) return false;
    },
    description: "Bookmark card",
    category: "Card detail view",
  });

  return (
    <TooltipForChild tooltip={tooltip} delayed targetIsDisabled={disabled} placement="right">
      <DSIconButton
        variant="tertiary"
        size="sm"
        active={Boolean(isActive)}
        icon={<DSIconBookmark />}
        onClick={handleClick}
        disabled={disabled}
      />
    </TooltipForChild>
  );
};

export const DocCardBottomButton = ({card, status}: {card: Card; status?: string}) => {
  const [doUpdate] = useMutation("cards", "update");
  const cardTypes = card.deck?.allowedCardTypes;
  const getDisabledReason = () => {
    if (card.isDoc) {
      if (cardTypes && cardTypes.includes("doc") && cardTypes.length === 1) {
        return "Current Deck only allows Doc Cards";
      }
    } else {
      if (cardTypes && !cardTypes.includes("doc")) {
        return "Current Deck does not allow Doc Cards";
      }
    }
    if (!status || card.isDoc) return null;
    if (status === "review") return "Close review before turning into Doc card";
    if (status === "blocked") return "Unblock card before turning into Doc card";
  };
  const disableReason = getDisabledReason();
  const handleClick = () => doUpdate({id: card.cardId, isDoc: !card.isDoc});

  const tooltip = disableReason || (card.isDoc ? "Turn into Task card" : "Turn into Doc card");

  return (
    <TooltipForChild tooltip={tooltip} targetIsDisabled={Boolean(disableReason)} placement="right">
      <DSIconButton
        variant="tertiary"
        size="sm"
        active={Boolean(card.isDoc)}
        icon={<DSIconDoc />}
        onClick={handleClick}
        disabled={Boolean(disableReason)}
      />
    </TooltipForChild>
  );
};

const SubTooltip = ({allSubs}: {allSubs: CardSubscription[]}) => {
  return (
    <Col width="480px" px="12px" py="8px" sp="12px" colorTheme="gray500">
      <Col sp="8px">
        <Box bold size={14} color="primary">
          Currently Watching
        </Box>
        {allSubs.length === 0 ? (
          <Box size={14} color="secondary">
            No one
          </Box>
        ) : (
          <Col sp="4px">
            {allSubs.map((s) => (
              <Row sp="4px" key={s.user && s.user.id} align="center">
                <Avatar user={s.user} size={14} />
                <Box color="primary" size={12}>
                  {s.user.name}
                </Box>
              </Row>
            ))}
          </Col>
        )}
      </Col>
      <Box
        px="12px"
        py="8px"
        borderWidth={1}
        borderColor="default"
        rounded={4}
        size={14}
        color="primary"
        bg="foreground"
      >
        Watchers receive a notification when a card is started, done, or deleted and when a
        conversation is started.
      </Box>
    </Col>
  );
};

export const SubscribeBottomButton = ({card, root}: {card: Card; root: Root}) => {
  const subs = card.$meta.find("cardSubscriptions", {$order: "createdAt"});
  const meId = root.loggedInUser?.$meta.get("id", null);
  const meSub = subs.find((s) => s.user.id === meId);

  const [addCard] = useMutation("watchings", "addCard");
  const [removeCard] = useMutation("watchings", "removeCard");

  const handleClick = () => {
    return meSub
      ? removeCard({cardId: card.cardId, userId: meId})
      : addCard({cardId: card.cardId, userId: meId});
  };

  return (
    <TooltipForChild tooltip={() => <SubTooltip allSubs={subs} />} placement="right" bg="gray800">
      <DSIconButton
        variant="tertiary"
        size="sm"
        active={Boolean(meSub)}
        icon={<DSIconEye />}
        onClick={handleClick}
        className={dsStyles.size[14]}
      />
    </TooltipForChild>
  );
};

const ArchiveButton = ({
  card,
  status,
  category,
  disabledHint,
}: {
  card: Card;
  status: string;
  category: string;
  disabledHint?: string | null;
}) => {
  const handleArchive = () => performArchiveAction(card);
  const mayArchive = !Boolean(disabledHint) && !(status === "blocked" || status === "review");
  const getTooltip = () => {
    if (!mayArchive) {
      return `You need to ${
        status === "review" ? "finish the review" : "unblock this card"
      } before you can archive the card.`;
    } else {
      return hasChildCards(card) ? "Archive this card and its sub cards" : "Archive this card";
    }
  };
  useGlobalKeyPress({
    key: "r",
    disabled: !mayArchive,
    fn: () => {
      performArchiveAction(card);
    },
    description: "Archive/unarchive card",
    category,
  });
  return (
    <TooltipForChild
      tooltip={disabledHint || getTooltip}
      targetIsDisabled={!mayArchive}
      placement="right"
    >
      <DSIconButton
        variant="tertiary"
        size="sm"
        onClick={handleArchive}
        disabled={!mayArchive}
        icon={<DSIconArchive />}
      />
    </TooltipForChild>
  );
};

const DeleteButton = ({
  onClick,
  disabledHint,
}: {
  card: Card;
  onClick: () => any;
  disabledHint?: string | null;
}) => {
  return (
    <TooltipForChild
      tooltip={disabledHint || "Delete this card"}
      targetIsDisabled={Boolean(disabledHint)}
      placement="right"
    >
      <DSIconButton
        variant="tertiary"
        size="sm"
        onClick={onClick}
        icon={<DSIconTrash />}
        disabled={Boolean(disabledHint)}
      />
    </TooltipForChild>
  );
};

const ResurrectButton = ({card, disabledHint}: {card: Card; disabledHint?: string | null}) => {
  const [doResurrect] = useMutation("cards", "update");

  return (
    <TooltipForChild
      tooltip={disabledHint || "Resurrect this card"}
      targetIsDisabled={Boolean(disabledHint)}
      placement="right"
    >
      <DSIconButton
        variant="tertiary"
        size="sm"
        onClick={() => doResurrect({id: card.cardId, visibility: "default"})}
        active
        icon={<DSIconTrash />}
        disabled={Boolean(disabledHint)}
      />
    </TooltipForChild>
  );
};

export const ArchiveOrDeleteBottomButton = (props: {
  card: Card;
  onClose: () => void;
  status: string;
  category: string;
  disabledHint?: string | null;
  arenaCtx: ArenaCtx;
}) => {
  const {card, status, onClose, category, disabledHint, arenaCtx} = props;
  const parentCardId = card.parentCard?.cardId;
  const handleCardDeletionDone = () => {
    if (parentCardId) {
      arenaCtx.routes.goToCard({cardId: parentCardId});
    } else {
      onClose();
    }
  };
  const handleDelete = () =>
    peformCardDeletion({cardId: card.cardId, onDone: handleCardDeletionDone});

  // deletion is possible for 'default' and 'archived' cards
  useGlobalKeyPress({
    key: "Backspace",
    disabled: Boolean(disabledHint) || card.visibility === "deleted",
    fn: () => {
      handleDelete();
    },
    description: "Delete card",
    category: "Card detail view",
  });

  switch (card.visibility) {
    case "default": {
      return (
        <ArchiveButton
          card={card}
          status={status}
          category={category}
          disabledHint={disabledHint}
        />
      );
    }
    case "archived": {
      return <DeleteButton card={card} onClick={handleDelete} disabledHint={disabledHint} />;
    }
    case "deleted": {
      return <ResurrectButton card={card} disabledHint={disabledHint} />;
    }
    default: {
      return null;
    }
  }
};
